import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Seo from '../../components/Seo/Seo';
import JsonLd from '../../components/JsonLd/JsonLd';
import { SITE_ORIGIN, SITE_NAME, blogImageUrl } from '../../data/businessInfo';
import { getPost, type BlogPost } from '../../api/blog';

// Dependency-free sanitizer for CMS article HTML before it is injected via
// dangerouslySetInnerHTML. Drops script-capable elements, strips on* handlers
// and neutralizes dangerous URL schemes; keeps ordinary formatting.
function sanitizeHtml(html: string): string {
	if (!html) return '';
	const doc = new DOMParser().parseFromString(html, 'text/html');
	const FORBIDDEN = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'FORM', 'INPUT', 'BUTTON', 'TEXTAREA', 'SELECT', 'LINK', 'META', 'BASE']);
	const URL_ATTRS = new Set(['href', 'src', 'xlink:href']);
	for (const el of Array.from(doc.body.querySelectorAll('*'))) {
		if (FORBIDDEN.has(el.tagName)) { el.remove(); continue; }
		for (const attr of Array.from(el.attributes)) {
			const name = attr.name.toLowerCase();
			if (name.startsWith('on') || name === 'style') { el.removeAttribute(attr.name); continue; }
			if (URL_ATTRS.has(name)) {
				const v = attr.value.toLowerCase().replace(/[\s\x00-\x1f]+/g, '');
				if (v.startsWith('javascript:') || v.startsWith('vbscript:') || v.startsWith('data:text/html')) el.removeAttribute(attr.name);
			}
		}
	}
	return doc.body.innerHTML;
}

function formatDate(dateStr: string) {
	if (!dateStr) return '';
	return new Date(dateStr).toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function ArticlePage() {
	const { slug } = useParams();
	const [article, setArticle] = useState<BlogPost | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);

	useEffect(() => {
		if (!slug) return;
		setLoading(true);
		setError(false);
		getPost(slug)
			.then((res) => setArticle(res.data.data))
			.catch(() => setError(true))
			.finally(() => setLoading(false));
	}, [slug]);

	if (loading) return <div className="container"><p className="note">Cikk betöltése…</p></div>;

	if (error || !article) {
		// SPA limitation: nginx already answered 200 for this URL, so noindex is the
		// strongest soft-404 signal a client route can emit.
		return (
			<div className="container article">
				<Seo title={`A cikk nem található - ${SITE_NAME}`} noindex />
				<h1>A cikk nem található</h1>
				<p style={{ marginTop: 12 }}><Link to="/blog" className="btn">Vissza a bloghoz</Link></p>
			</div>
		);
	}

	const stripped = (article.content || '').replace(/^\s*<h[1-6]\b[^>]*>[\s\S]*?<\/h[1-6]>\s*/i, '');
	const body = sanitizeHtml(stripped);
	const canonicalPath = `/blog/${article.slug || slug}`;

	// "Frissítve" date: modification wins, then publish, then creation. Read the date part
	// straight from the string (no timezone conversion) to match the PHP sites' SeoHuDate
	// and avoid the PHP-Berlin / MySQL-UTC skew shifting the day around midnight.
	const updatedRaw = article.datetime_modified || article.published_at || article.datetime_created || '';
	const updatedIso = updatedRaw.slice(0, 10);
	const updatedHu = /^\d{4}-\d{2}-\d{2}$/.test(updatedIso)
		? `${updatedIso.slice(0, 4)}. ${updatedIso.slice(5, 7)}. ${updatedIso.slice(8, 10)}.`
		: '';

	// Author / szakmai lektor attribution — matches akkusegely SeoRenderAuthorLine wording.
	const authorName = (article.author_name || '').trim();
	const reviewerName = (article.reviewer_name || '').trim();
	const reviewerCredential = (article.reviewer_credential || '').trim();
	const attributionParts = [
		authorName && `Szerző: ${authorName}`,
		reviewerName && `Szakmai lektor: ${reviewerName}${reviewerCredential ? `, ${reviewerCredential}` : ''}`,
	].filter(Boolean) as string[];

	// Published chain FAQ rows -> visible GYIK block + FAQPage JSON-LD (same rows, no drift).
	const faqItems = (article.faq || []).filter((f) => f.question?.trim() && f.answer?.trim());
	const faqJsonLd =
		faqItems.length > 0
			? {
					'@context': 'https://schema.org',
					'@type': 'FAQPage',
					mainEntity: faqItems.map((f) => ({
						'@type': 'Question',
						name: f.question,
						acceptedAnswer: { '@type': 'Answer', text: f.answer },
					})),
			  }
			: null;

	return (
		<div className="container">
			<Seo
				title={`${article.meta_title || article.title} | ${SITE_NAME}`}
				description={article.meta_description || article.lead || undefined}
				canonicalPath={canonicalPath}
				type="article"
				image={article.image ? blogImageUrl(article.image) : undefined}
				ogTitle={article.og_title || undefined}
				ogDescription={article.og_description || undefined}
			/>
			<JsonLd
				data={{
					'@context': 'https://schema.org',
					'@type': 'BlogPosting',
					mainEntityOfPage: `${SITE_ORIGIN}${canonicalPath}`,
					headline: article.title,
					...(article.meta_description || article.lead
						? { description: article.meta_description || article.lead }
						: {}),
					image: blogImageUrl(article.image),
					datePublished: article.datetime_created,
					dateModified: article.datetime_modified || article.datetime_created,
					inLanguage: 'hu',
					author: { '@id': `${SITE_ORIGIN}/#organization` },
					publisher: { '@id': `${SITE_ORIGIN}/#organization` },
					...(article.category?.name ? { articleSection: article.category.name } : {}),
				}}
			/>
				{faqJsonLd && <JsonLd data={faqJsonLd} />}

			<article className="article">
				<h1>{article.title}</h1>
				<div className="article__date">{formatDate(article.datetime_created)}{article.category?.name ? ` · ${article.category.name}` : ''}</div>
				{updatedHu && (
					<p className="article__updated">Frissítve: <time dateTime={updatedIso}>{updatedHu}</time></p>
				)}
				{attributionParts.length > 0 && (
					<p className="article__author">{attributionParts.join(' · ')}</p>
				)}
				<div className="article__content" dangerouslySetInnerHTML={{ __html: body }} />
				{faqItems.length > 0 && (
					<section className="article-faq">
						<h2 className="article-faq__heading">Gyakori kérdések</h2>
						{faqItems.map((item, i) => (
							<div key={i} className="article-faq__item">
								<h3 className="article-faq__question">{item.question}</h3>
								<p className="article-faq__answer">{item.answer}</p>
							</div>
						))}
					</section>
				)}
				<p style={{ marginTop: 32 }}><Link to="/blog" className="btn">← Vissza a bloghoz</Link></p>
			</article>

			{article.blogs?.length > 0 && (
				<div style={{ marginTop: 20, paddingBottom: 40 }}>
					<h2 style={{ fontSize: '1.2rem', marginBottom: 14 }}>Ajánlott cikkek</h2>
					<div className="blog-grid">
						{article.blogs.map((r) => (
							<article key={r.id} className="blog-card">
								<div className="blog-card__body">
									<h2 className="blog-card__title"><Link to={`/blog/${r.slug}`}>{r.title}</Link></h2>
									<Link className="blog-card__more" to={`/blog/${r.slug}`}>Elolvasom →</Link>
								</div>
							</article>
						))}
					</div>
				</div>
			)}
		</div>
	);
}
